import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

interface EmptyStateOverlayProps {
  /** When true, dims children and floats the overlay card on top. When false, renders children as-is. */
  active: boolean;
  /** Lucide icon displayed in the circular badge above the message. */
  icon: LucideIcon;
  /** Primary headline shown on the overlay card. */
  message: string;
  /** Optional supporting text below the headline. */
  description?: string;
  /**
   * Optional control below the description — for overlays that state a blocker the operator
   * can actually resolve, so the fix is one click away rather than something to go and find.
   */
  action?: ReactNode;
  /** Content that's visually masked while `active`. */
  children: ReactNode;
  /** Optional classes applied to the outer relative wrapper. */
  className?: string;
  /** `data-testid` for the overlay card (default `empty-state-overlay`); derives `-message`, `-description`. */
  testId?: string;
}

/**
 * Renders `children` with an empty-state card floating on top of a blurred,
 * dimmed copy of the content — used to communicate "no data yet" while keeping
 * the underlying layout recognizable.
 */
export function EmptyStateOverlay({
  active,
  icon: Icon,
  message,
  description,
  action,
  children,
  className,
  testId = 'empty-state-overlay',
}: EmptyStateOverlayProps) {
  if (!active) return <>{children}</>;

  return (
    <div className={cn('relative', className)}>
      <div aria-hidden className="pointer-events-none opacity-40 blur-[2px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="flex max-w-sm flex-col items-center gap-3 rounded-xl border bg-white px-8 py-6 text-center shadow-lg"
          data-testid={testId}
        >
          <div className="bg-accent-100 flex h-12 w-12 items-center justify-center rounded-full">
            <Icon className="text-accent-main h-6 w-6" aria-hidden />
          </div>
          <p className="text-sm leading-snug font-semibold" data-testid={`${testId}-message`}>
            {message}
          </p>
          {description !== undefined && description !== '' && (
            <p className="text-muted-foreground text-xs" data-testid={`${testId}-description`}>
              {description}
            </p>
          )}
          {action && <div className="mt-1">{action}</div>}
        </div>
      </div>
    </div>
  );
}
